import * as THREE from 'three';
import { CORNERS, elevationToY } from './HexMetrics';
import { DIRECTIONS, axialKey, axialToWorld } from './HexCoordinates';
import { HexCellData, cornerKey, isWater, waterLevel } from './HexSurface';

/**
 * Water surfaces, built as their own mesh rather than as part of the terrain.
 *
 * Keeping it separate is what gives the hard edge at the shore: two meshes
 * cannot blend into one another, so the blue simply stops where the water cells
 * stop and the sand of the terrain takes over. Interleaving water into the
 * terrain buffer would have meant splitting every shore vertex to keep its two
 * colours apart.
 *
 * It also leaves room for the surface to become its own thing later — animated,
 * translucent, or driven by a simulation — without any of that reaching into
 * the terrain.
 *
 * # More than one surface
 *
 * The sea is one sheet at level zero. **Lakes are not**: each stands at its own
 * height, sent per cell by the generator, and there are thousands of them
 * across a world — six in seven of them above sea level. They are built into
 * the same mesh because they are the same material and the same idea; what they
 * cannot share is a vertex. Two waters meeting at a hex corner sit at different
 * heights there, so the vertices are pooled **per level**: within one body they
 * are shared and the sheet has no internal edges, and between two bodies they
 * are simply two different points that happen to be above one another.
 */

/**
 * How far from land the swell is drawn, in cells. Past this the water is open
 * sea and nothing travels across it.
 *
 * Eight rather than the three the first version used: a band that has to run
 * out from the beach needs somewhere to run to, and three cells is one band
 * width.
 */
const SHORE_REACH = 8;

/**
 * Distance from land for every water cell of a chunk, in cells.
 *
 * A flood fill outwards from the coast rather than a search outwards from each
 * cell: the search is `6 × reach` lookups per cell and gets the diagonals
 * wrong, because stepping in one of the six directions is not the same as hex
 * distance. The fill is one pass over the cells and is exact.
 *
 * **The fill runs over the chunk plus a margin of `SHORE_REACH` cells around
 * it**, not over the chunk alone. A cell near the border can be five cells from
 * a coast that lies in the neighbouring chunk; seeded from the chunk's own land
 * only, it would come out as open sea while the same cell drawn by the
 * neighbour came out mid-swell — and the band would break along the border. The
 * margin is there to be had: a chunk is only meshed once all six of its
 * neighbours have streamed in, and they reach eighteen cells past it.
 */
function shoreDistances(
  members: HexCellData[],
  world: Map<number, HexCellData>
): Map<number, number> {
  const distance = new Map<number, number>();
  let front: HexCellData[] = [];

  // The chunk and everything within reach of it, which is the ground the fill
  // is allowed to cross.
  const domain = new Map<number, HexCellData>();
  let edge: HexCellData[] = [];
  for (const cell of members) {
    domain.set(axialKey(cell.q, cell.r), cell);
    edge.push(cell);
  }

  // One cell more than the swell reaches: a corner vertex of a border cell asks
  // about the cell **beyond** the chunk, and that cell needs its own full reach
  // to answer the same as it does inside its own chunk. Measured without it:
  // four vertices out of 464 on the borders were exactly one cell apart.
  for (let step = 0; step < SHORE_REACH + 1; step++) {
    const next: HexCellData[] = [];
    for (const cell of edge) {
      for (const direction of DIRECTIONS) {
        const key = axialKey(cell.q + direction.q, cell.r + direction.r);
        if (domain.has(key)) continue;

        const there = world.get(key);
        if (!there) continue;

        domain.set(key, there);
        next.push(there);
      }
    }
    edge = next;
  }

  // Seed from every piece of land in that ground.
  for (const cell of domain.values()) {
    if (isWater(cell)) continue;
    distance.set(axialKey(cell.q, cell.r), 0);
    front.push(cell);
  }

  for (let step = 1; step <= SHORE_REACH && front.length > 0; step++) {
    const next: HexCellData[] = [];

    for (const cell of front) {
      for (const direction of DIRECTIONS) {
        const key = axialKey(cell.q + direction.q, cell.r + direction.r);
        if (distance.has(key)) continue;

        // Spreading across everything, water and land alike, so what comes out
        // is the **hex distance to the nearest coast** rather than a distance
        // measured along the water. Along the water it depends on how much
        // water the fill can see: a cell reached round a headland inside one
        // chunk's ground is reached across it inside the neighbour's, and the
        // two disagree. Measured before this: four vertices out of 464 on chunk
        // borders came out a cell apart, which is a band with a step in it.
        if (!domain.has(key)) continue;

        distance.set(key, step);
        next.push(domain.get(key)!);
      }
    }

    front = next;
  }

  return distance;
}

/**
 * The sea, with a swell running out from the shore.
 *
 * Procedural, with no texture and no sprite sheet: a drawing of waves in a file
 * has to be made for one zoom and is wrong at every other, while a function of
 * position and time scales itself.
 *
 * **What is drawn is one thing, not three.** A band of very slightly lighter
 * water, parallel to the coast, gathering out at sea and running **in towards
 * the beach**, where it thins out and is gone. The lightening is a few percent
 * — the sea changes shade rather than growing white marks on it.
 *
 * That replaces a first attempt with white crests on the open water and a foam
 * band at the beach. Both were the same mistake in two sizes: **a mark on the
 * water instead of a movement of it.** The crests read as a lattice of bars,
 * and even once their scale was fixed they were still bright shapes with edges,
 * sitting on the surface rather than being part of it.
 *
 * On top of that a **Fresnel term**: the sea looked at from above is dark and
 * from a glancing angle takes the colour of the sky — which is handed in from
 * the day cycle, so the water turns with the sunset like everything else.
 *
 * The swell fades out as the camera pulls back, for the same reason everything
 * fine does: at a wide zoom a band is a pixel, and a moving pixel-wide pattern
 * is noise.
 */
export function createWaterMaterial(): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: 0x2e7fc9,
    roughness: 0.25,
    metalness: 0.1,
  });

  const uniforms = {
    uTime: { value: 0 },
    uSky: { value: new THREE.Color(0xbfd8ff) },
    /** 1 up close, towards 0 when zoomed out. */
    uDetail: { value: 1 },
    /** What a band shifts the water towards: its own colour, lifted. */
    uSwell: { value: new THREE.Color(0x4f9ad8) },
  };
  material.userData.uniforms = uniforms;

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uniforms.uTime;
    shader.uniforms.uSky = uniforms.uSky;
    shader.uniforms.uDetail = uniforms.uDetail;
    shader.uniforms.uSwell = uniforms.uSwell;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
        attribute float shore;
        varying float vShore;
        varying vec2 vSurface;`
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vShore = shore;
        vSurface = (modelMatrix * vec4(position, 1.0)).xz;`
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform float uTime;
        uniform vec3 uSky;
        uniform float uDetail;
        uniform vec3 uSwell;
        varying float vShore;
        varying vec2 vSurface;`
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>

        // Distance from the beach, 0 at the sand and 1 eight cells out.
        //
        // Named offshore and not the obvious thing: the obvious thing is a
        // GLSL keyword, the shader failed to compile on it, and a material that
        // fails to compile draws nothing — so the sea simply disappeared.
        float offshore = 1.0 - vShore;

        // A train of bands rather than one: the phase is the distance from the
        // beach plus time, so every whole number of it is a crest and they all
        // travel the same way. Adding time rather than subtracting it is what
        // sends them **towards** the shore — the sign here is the whole
        // direction of the sea.
        //
        // 2,5 bands across the eight cells of swell, one starting every 3,5
        // seconds, each moving about nine tenths of a cell a second.
        float phase = offshore * 2.5 + uTime * 0.29;
        float band = 1.0 - smoothstep(0.0, 0.42, abs(fract(phase) - 0.5) * 2.0);

        // Gathering as they come in and gone by the time they reach the sand:
        // the water runs out on the beach rather than stopping against it, and
        // no band appears out of nothing at the outer edge of the swell.
        float build = smoothstep(0.02, 0.22, offshore) * (1.0 - smoothstep(0.72, 1.0, offshore));

        // Some unevenness along the coast, so it is not one clean arc across a
        // whole bay.
        float ripple = 0.75 + 0.25 * sin(dot(vSurface, vec2(0.71, 0.71)) * 1.3 + uTime * 0.3);

        // Nothing at all where there is no shore to leave from.
        float sea = smoothstep(0.0, 0.15, vShore);

        diffuseColor.rgb = mix(
          diffuseColor.rgb,
          uSwell,
          clamp(band * build * ripple * sea, 0.0, 1.0) * uDetail * 0.5
        );`
      )
      .replace(
        '#include <lights_fragment_end>',
        `#include <lights_fragment_end>

        // Sky in the surface: full-on at a glancing angle, nothing looking
        // straight down. The colour comes from the day cycle, so dusk is on the
        // water as well as on the land.
        // normal and vViewPosition are both in view space here, and normal is
        // the one the lighting itself used — reading vNormal instead would
        // disagree with it wherever the surface is flat-shaded.
        vec3 toEye = normalize(vViewPosition);
        float grazing = pow(1.0 - clamp(dot(normal, toEye), 0.0, 1.0), 3.0);
        reflectedLight.indirectDiffuse += uSky * grazing * 0.45;`
      );
  };

  return material;
}

/**
 * Flat hexagons over every water cell in `members`.
 *
 * Returns null when the chunk holds no water, so callers can skip adding an
 * empty mesh to the scene.
 */
export function buildWaterGeometry(
  members: HexCellData[],
  world: Map<number, HexCellData>
): THREE.BufferGeometry | null {
  const positions: number[] = [];
  const shores: number[] = [];
  const indices: number[] = [];
  /** Shared corners, one pool per water level. */
  const vertices = new Map<number, Map<number, number>>();

  // One fill for the whole chunk, then a lookup per vertex.
  const distances = shoreDistances(members, world);

  /** 1 against the beach, 0 at the far edge of the swell and beyond it. */
  const shoreOf = (cell: HexCellData): number => {
    const step = distances.get(axialKey(cell.q, cell.r));
    if (step === undefined) return 0;
    return Math.max(0, 1 - (step - 1) / SHORE_REACH);
  };

  /**
   * A shared corner takes the **largest** shoreness of the cells that meet
   * there, so the band is continuous along the coast: taking whichever cell
   * happened to create the vertex would leave the surf stopping and starting
   * with the order the fans were built in.
   */
  const cornerShore = (cell: HexCellData, corner: number): number => {
    let most = shoreOf(cell);
    for (const step of [(corner + 5) % 6, corner]) {
      const there = world.get(
        axialKey(cell.q + DIRECTIONS[step].q, cell.r + DIRECTIONS[step].r)
      );
      if (there && isWater(there)) most = Math.max(most, shoreOf(there));
    }
    return most;
  };

  const vertexAt = (cell: HexCellData, corner: number, level: number): number => {
    let pool = vertices.get(level);
    if (!pool) {
      pool = new Map();
      vertices.set(level, pool);
    }

    const key = cornerKey(cell, corner);
    const existing = pool.get(key);
    if (existing !== undefined) return existing;

    const { x, z } = axialToWorld(cell.q, cell.r);
    const offset = CORNERS[corner];
    const index = positions.length / 3;

    positions.push(x + offset.x, elevationToY(level), z + offset.z);
    shores.push(cornerShore(cell, corner));
    pool.set(key, index);
    return index;
  };

  for (const cell of members) {
    const level = waterLevel(cell);
    if (level === null) continue;

    const { x, z } = axialToWorld(cell.q, cell.r);

    // Centres belong to one cell each, so they are never looked up and never
    // go in the shared map
    const center = positions.length / 3;
    positions.push(x, elevationToY(level), z);
    shores.push(shoreOf(cell));

    for (let corner = 0; corner < 6; corner++) {
      indices.push(
        center,
        vertexAt(cell, corner, level),
        vertexAt(cell, (corner + 1) % 6, level)
      );
    }
  }

  if (indices.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(indices);
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute('shore', new THREE.Float32BufferAttribute(shores, 1));
  geometry.computeVertexNormals();
  return geometry;
}
